import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Badge,
    Form,
    InputGroup,
    Modal,
    Alert,
    Spinner,
    Pagination,
} from 'react-bootstrap';
import {
    FaEnvelope,
    FaSearch,
    FaTrash,
    FaUsers,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaShare,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import NewsletterService from '../../services/newsletterService';

const NewsletterManagement = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSendModal, setShowSendModal] = useState(false);
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [sending, setSending] = useState(false);

    // Pagination
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1,
    });
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadData();
        loadCampaigns();
    }, [currentPage, searchTerm]);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await NewsletterService.getSubscribers(currentPage, 20, searchTerm);
            if (response.data) {
                setSubscribers(response.data.subscribers || []);
                setStats(response.data.stats || { total: 0, active: 0, unsubscribed: 0 });
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load subscribers:', error);
            toast.error('Failed to load subscribers');
        } finally {
            setLoading(false);
        }
    };

    const loadCampaigns = async () => {
        try {
            const response = await NewsletterService.getCampaigns();
            setCampaigns(response.data || []);
        } catch (error) {
            console.error('Failed to load campaigns:', error);
        }
    };

    const handleDelete = async (id, email) => {
        if (window.confirm(`Are you sure you want to delete ${email}?`)) {
            try {
                await NewsletterService.deleteSubscriber(id);
                toast.success('Subscriber deleted successfully');
                loadData();
            } catch (error) {
                toast.error('Failed to delete subscriber');
            }
        }
    };

    const handleSendCampaign = async () => {
        if (!subject.trim()) {
            toast.error('Please enter a subject');
            return;
        }
        if (!content.trim()) {
            toast.error('Please enter email content');
            return;
        }

        try {
            setSending(true);
            const response = await NewsletterService.sendCampaign(subject, content);
            toast.success(response.message || `Newsletter sent to ${response.data?.sent_count || 0} subscribers`);
            setShowSendModal(false);
            setSubject('');
            setContent('');
            loadCampaigns();
        } catch (error) {
            console.error('Failed to send campaign:', error);
            toast.error(error.response?.data?.message || 'Failed to send newsletter');
        } finally {
            setSending(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        loadData();
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Pagination handlers
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
            setCurrentPage(pageNumber);
        }
    };

    const renderPagination = () => {
        const { current_page, total_pages } = pagination;
        if (total_pages <= 1) return null;

        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(total_pages, startPage + maxVisible - 1);

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(current_page - 1)}
                disabled={current_page === 1}
            />
        );

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item key={i} active={i === current_page} onClick={() => handlePageChange(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < total_pages) {
            if (endPage < total_pages - 1) items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            items.push(
                <Pagination.Item key={total_pages} onClick={() => handlePageChange(total_pages)}>
                    {total_pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(current_page + 1)}
                disabled={current_page === total_pages}
            />
        );

        return <Pagination>{items}</Pagination>;
    };

    return (
        <div>
            <Header />
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>
                        <FaEnvelope className="me-2 text-primary" />
                        Newsletter Management
                    </h3>
                    <Button variant="primary" onClick={() => setShowSendModal(true)}>
                        <FaShare className="me-2" /> Send Newsletter
                    </Button>
                </div>

                {/* Stats Cards */}
                <Row className="mb-4">
                    <Col md={4}>
                        <Card className="text-center shadow-sm border-0 bg-primary text-white">
                            <Card.Body>
                                <FaUsers size={30} className="mb-2" />
                                <h2>{stats.total || 0}</h2>
                                <p>Total Subscribers</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="text-center shadow-sm border-0 bg-success text-white">
                            <Card.Body>
                                <FaCheckCircle size={30} className="mb-2" />
                                <h2>{stats.active || 0}</h2>
                                <p>Active Subscribers</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="text-center shadow-sm border-0 bg-secondary text-white">
                            <Card.Body>
                                <FaTimesCircle size={30} className="mb-2" />
                                <h2>{stats.unsubscribed || 0}</h2>
                                <p>Unsubscribed</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Search Bar */}
                <div className="mb-3">
                    <InputGroup style={{ width: '300px' }}>
                        <Form.Control
                            placeholder="Search by email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <Button variant="outline-primary" onClick={handleSearch}>
                            <FaSearch />
                        </Button>
                    </InputGroup>
                </div>

                {/* Subscribers Table */}
                <Card className="shadow-sm mb-4">
                    <Card.Body>
                        <h5 className="mb-3">Subscribers List</h5>
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : subscribers.length === 0 ? (
                            <Alert variant="info">No subscribers found.</Alert>
                        ) : (
                            <>
                                <Table striped hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Subscribed Date</th>
                                            <th>Status</th>
                                            <th>Source</th>
                                            <th>IP Address</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subscribers.map((sub) => (
                                            <tr key={sub.id}>
                                                <td>{sub.email}</td>
                                                <td>{formatDate(sub.subscribed_at)}</td>
                                                <td>
                                                    <Badge bg={sub.status === 'active' ? 'success' : 'secondary'}>
                                                        {sub.status}
                                                    </Badge>
                                                </td>
                                                <td>{sub.source || '-'}</td>
                                                <td>{sub.ip_address || '-'}</td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(sub.id, sub.email)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <div className="d-flex justify-content-center mt-3">
                                    {renderPagination()}
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {/* Campaign History */}
                <Card className="shadow-sm">
                    <Card.Body>
                        <h5 className="mb-3">Sent Campaigns History</h5>
                        {campaigns.length === 0 ? (
                            <Alert variant="info">No campaigns sent yet.</Alert>
                        ) : (
                            <Table striped hover responsive>
                                <thead>
                                    <tr>
                                        <th>Subject</th>
                                        <th>Sent Date</th>
                                        <th>Recipients</th>
                                        <th>Sent By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((campaign) => (
                                        <tr key={campaign.id}>
                                            <td><strong>{campaign.subject}</strong></td>
                                            <td>{formatDate(campaign.sent_at)}</td>
                                            <td><Badge bg="info">{campaign.recipients_count}</Badge></td>
                                            <td>{campaign.sent_by_name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </div>

            {/* Send Newsletter Modal */}
            <Modal show={showSendModal} onHide={() => setShowSendModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaShare className="me-2 text-primary" />
                        Send Newsletter
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info">
                        This will send an email to all <strong>{stats.active || 0}</strong> active subscribers.
                    </Alert>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter email subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email Content <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                placeholder="Write your email content here. You can use HTML tags for formatting."
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                You can use HTML for formatting: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSendModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSendCampaign} disabled={sending}>
                        {sending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <FaShare className="me-2" />
                                Send to {stats.active || 0} Subscribers
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default NewsletterManagement;